import React, { useContext, useState } from "react";
import {
  useTableCompositeNavigation,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Button,
  makeStyles,
} from "@fluentui/react-components";
import { ChevronRight16Regular } from "@fluentui/react-icons";
import Loader from "./Loader";
import ErrorAlert from "./ErrorAlert";
import { writeToSheet } from "../../utils/officedocumenthelper";
import { useNavigate } from "react-router-dom";
import { PortfolioService } from "../../service/PortfolioService";
import { SelectionsetService } from "../../service/SelectionsetService";
import { AttributeConfigService } from "../../service/AttributeConfigService";
import { IndexService } from "../../service/IndexService";
import { PeerGroupService } from "../../service/PeerGroupService";
import { InvestorService } from "../../service/InvestorService";
import { CalculationsetService } from "../../service/CalculationsetService";
import { AppContext } from "./context/AppContext";
import { getStringFromStorage } from "../../utils/storagehelper";
import { ProfileSettings } from "../../types/app.storage.enums";
import {
  PortfoliosHeaders,
  SelectionSetHeaders,
  AttributeHeaders,
  IndexHeaders,
  PeerGroupHeaders,
  InvestorHeaders,
  CalculationSetHeaders,
} from "../../constants/app.header.constants";
import { MasterDataMenus } from "../../types/app.menu.enums";
import { isValidUser, propsToValueArray } from "../../utils/common";
import { extractArrayPayload, getApiErrorMessage } from "../../utils/apierror";

const portfolioSet = new Set([
  "portfolioID",
  "portfolioName",
  "portfolioCurrency",
  "portfolioType",
  "portfolioGroupID",
  "portfolioGroup",
]);

const selectionSet = new Set(["id", "name", "type", "isFolder", "parentID"]);

const attributesSet = new Set([
  "name",
  "forUseWithBurgissGetAttributeFunction",
  "forUseWithInvestmentCalcFilterParameter",
  "forUseWithHoldingCalcFilterParameter",
]);

const indexesSet = new Set([
  "benchmarkID",
  "benchmarkName",
  "minBenchmarkDate",
  "maxBenchmarkDate",
  "burgissManaged",
  "universeAnalyticsEligible",
  "useWithUniverseAnalytics",
]);

const peerGroupSet = new Set(["iqFilterID", "filterName", "vehicleType", "burgissManaged"]);

const investorsSet = new Set(["id", "name"]);

const calculationSet = new Set(["filterSetID", "filterSetName"]);

const useStyles = makeStyles({
  downloadButton: {
    width: "85px",
  },
  outlineButton: {
    borderBottomWidth: "2px",
    borderTopWidth: "2px",
    borderLeftWidth: "2px",
    borderRightWidth: "2px",
    borderTopColor: "#0a2e4a",
    borderBottomColor: "#0a2e4a",
    borderLeftColor: "#0a2e4a",
    borderRightColor: "#0a2e4a",
  },
});
const MasterData = React.memo<{ hasLicense: boolean; hasBasicAccess: boolean }>(
  ({ hasLicense: isLicense, hasBasicAccess: isBasicAccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { tableRowTabsterAttribute, tableTabsterAttribute, onTableKeyDown } = useTableCompositeNavigation();
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const styles = useStyles();

    const resolveProfileId = () => {
      return getStringFromStorage(ProfileSettings.ProfileId) || context.selectedProfile?.profileID || "";
    };

    const downloadMasterData = async (request, fieldSet, headers, entityName) => {
      setErrorMessage("");
      try {
        const result = await request;
        const rows = extractArrayPayload(result);
        if (!rows) {
          throw new Error(getApiErrorMessage(result, `Unable to download ${entityName}. Please try again.`));
        }
        const values = propsToValueArray(rows, fieldSet);
        await writeToSheet(headers, values);
      } catch (error) {
        setErrorMessage(getApiErrorMessage(error, `Unable to download ${entityName}. Please try again.`));
      } finally {
        setIsLoading(false);
      }
    };

    const handleMenuClick = async (menu) => {
      if (isValidUser()) {
        navigate("/home");
        return;
      }

      if (menu === MasterDataMenus.Investments) {
        navigate("/investment");
        return;
      }

      const profileId = resolveProfileId();
      if (!profileId) {
        setErrorMessage("Please select a profile before downloading.");
        return;
      }

      setErrorMessage("");
      setIsLoading(true);

      try {
        switch (menu) {
          case MasterDataMenus.Portfolios:
            await downloadMasterData(
              new PortfolioService().getPortfolios(profileId),
              portfolioSet,
              PortfoliosHeaders,
              MasterDataMenus.Portfolios
            );
            break;

          case MasterDataMenus.SelectionSet:
            await downloadMasterData(
              new SelectionsetService().getSelectionsets(profileId),
              selectionSet,
              SelectionSetHeaders,
              MasterDataMenus.SelectionSet
            );
            break;

          case MasterDataMenus.Attributes:
            await downloadMasterData(
              new AttributeConfigService().getAttributes(profileId),
              attributesSet,
              AttributeHeaders,
              MasterDataMenus.Attributes
            );
            break;

          case MasterDataMenus.Indexes:
            await downloadMasterData(
              new IndexService().getIndexes(profileId),
              indexesSet,
              IndexHeaders,
              MasterDataMenus.Indexes
            );
            break;

          case MasterDataMenus.PeerGroups:
            await downloadMasterData(
              new PeerGroupService().getPeerGroups(profileId),
              peerGroupSet,
              PeerGroupHeaders,
              MasterDataMenus.PeerGroups
            );
            break;

          case MasterDataMenus.Investors:
            await downloadMasterData(
              new InvestorService().getInvestors(profileId),
              investorsSet,
              InvestorHeaders,
              MasterDataMenus.Investors
            );
            break;

          case MasterDataMenus.CalculationSet:
            await downloadMasterData(
              new CalculationsetService().getCalculationsets(profileId),
              calculationSet,
              CalculationSetHeaders,
              MasterDataMenus.CalculationSet
            );
            break;

          default:
            setIsLoading(false);
            break;
        }
      } catch (error) {
        setErrorMessage(getApiErrorMessage(error));
        setIsLoading(false);
      }
    };
    const handleMenuRights = (menu) => {
      if (menu === MasterDataMenus.Investors) return isLicense;
      else return isBasicAccess;
    };

    return (
      <>
        <div role="tabpanel" aria-labelledby="data">
          <Table role="grid" noNativeElements onKeyDown={onTableKeyDown} {...tableTabsterAttribute}>
            <TableBody>
              {Object.entries<MasterDataMenus>(MasterDataMenus).map(([key, value]): any => {
                return (
                  handleMenuRights(value) && (
                    <TableRow tabIndex={0} key={key} {...tableRowTabsterAttribute}>
                      <TableCell tabIndex={0} role="gridcell">
                        <TableCellLayout>{value}</TableCellLayout>
                      </TableCell>
                      <TableCell role="gridcell">
                        <TableCellLayout dir="rtl">
                          {value === MasterDataMenus.Investments ? (
                            <Button
                              dir="ltr"
                              size="small"
                              appearance="outline"
                              icon={<ChevronRight16Regular />}
                              iconPosition="after"
                              className={styles.outlineButton}
                              disabled={isLoading}
                              onClick={() => handleMenuClick(value)}
                            >
                              Proceed
                            </Button>
                          ) : (
                            <Button
                              dir="ltr"
                              size="small"
                              appearance="primary"
                              className={styles.downloadButton}
                              disabled={isLoading}
                              onClick={() => handleMenuClick(value)}
                            >
                              Download
                            </Button>
                          )}
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  )
                );
              })}
            </TableBody>
          </Table>
        </div>
        <ErrorAlert message={errorMessage} onDismiss={() => setErrorMessage("")} />
        {isLoading && <Loader></Loader>}
      </>
    );
  }
);
MasterData.displayName = "Data";

export default MasterData;
